<template>
    <div>
        <PostCommentNsfw v-if="comment.is_nsfw"></PostCommentNsfw>
        <PostCommentEdit
            v-else-if="edit"
            ref="commentInput"
            :post-id="post.postDetails.id"
            :comment="comment"
            :context="post.postDetails.context"
            :context-id="postContextId"
            @close-comment-edit="edit = false"
        ></PostCommentEdit>
        <template v-else>
            <div class="flex items-center">
                <div>
                    <UserAvatar
                        :karma-points="user && !user.isDeleted ? user.karmaPoints : null"
                        :link="user && !user.isDeleted ? user.profileUrl : null"
                        :name="user ? user.name : anonymousUser.name"
                        :user-id="user ? user.id : 0"
                        :anonymous-user="userPreview"
                        :anonymous-role="userPreview ? userPreview.user_role : null"
                        :is-anonymous-user="comment.isAnonymous"
                        :animated-avatar="user ? user.gamify_avatar_url : null"
                        :picture="user ? user.profilePictureUrl : `/${anonymousUser.icon}`"
                        show-details
                        :show-admin-badge="user ? user.hasAdminBadge : false"
                        class="exclude-verified flex-grow flex-none mr-2"
                        size="comment"
                    >
                        <template #labels>
                            <Tag
                                v-if="!user && comment.isOwner"
                                color-class="bg-slate-400 text-white"
                                class="ml-1"
                                :text="$t('post:labels:you')"
                                :size="TagSize.SM"
                            ></Tag>
                        </template>
                    </UserAvatar>
                </div>
                <div class="ml-auto">
                    <BtnMenu
                        v-if="screenSize !== 'small'"
                        variant="tonal"
                        size="xs"
                        left-icon="more"
                        menu-placement="bottom-end"
                        :options="options"
                    />
                </div>
            </div>
            <div class="w-full pl-10">
                <CollapsibleText
                    :content="comment.content"
                    show-more-appearance="font-roboto text-slate-400"
                    class="leading-6 highlight-links"
                />
                <PostImages
                    v-if="commentImages.length"
                    :files="commentImages"
                    class="mt-3"
                />
                <div :class="['flex flex-col md:mt-3', isDocumentPage ? 'md:flex-row-reverse' : '']">
                    <template v-if="comment.isBest">
                        <Btn
                            v-if="post.isOwner"
                            variant="filled"
                            :class="bestAnswerBtnClass"
                            color="green-100"
                            content-color="green-500"
                            :size="'sm'"
                            :label="mouseIsOverBestCommentButton ? $t('post:change') : $t('post:bestAnswer')"
                            @click="toggleBestComment"
                            @mouseleave="mouseIsOverBestCommentButton = false"
                            @mouseover="mouseIsOverBestCommentButton = true"
                        />
                        <Btn
                            v-else
                            :label="$t('post:bestAnswer')"
                            :size="'sm'"
                            color="green-100"
                            content-color="green-500"
                            variant="filled"
                            :class="bestAnswerBtnClass"
                        />
                    </template>
                    <div :class="['flex-1 flex items-center gap-x-4 mt-3', isDocumentPage ? ' md:mt-0' : '']">
                        <BtnLike
                            :enabled="!isCommentOwner"
                            :active="comment.userLiked"
                            :likes="comment.upvotes"
                            :request-url="
                                route(`post.answer.upvote`, {
                                    context: post.postDetails.context,
                                    id: comment.id,
                                })
                            "
                            size="xs"
                        />
                        <Btn
                            v-if="canSelectBestAnswer && screen.minMD"
                            variant="text"
                            class="!font-light !text-sm"
                            icon="checkmark-outlined"
                            size="xs"
                            :label="$t('post:markBestAnswer')"
                            compact
                            @click="toggleBestComment"
                        />
                        <span class="text-xs text-slate-400 truncate">{{ comment.editDateHuman }}</span>
                        <BtnMenu
                            v-if="screenSize === 'small'"
                            variant="tonal"
                            size="xs"
                            left-icon="more"
                            :options="options"
                        />
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import { defineAsyncComponent } from 'vue';
import { mapGetters } from 'vuex';
import route from '@router/route';
import BtnLike from '@components/BtnLike';
import CollapsibleText from '@components/CollapsibleText';
import { TagSize } from '@components/Tag';
import PostCommentEdit from './PostCommentEdit.vue';
import PostCommentNsfw from './PostCommentNsfw.vue';
import PostImages from './PostImages.vue';

export default {
    name: 'PostComment',
    components: {
        PostCommentNsfw,
        BtnLike,
        CollapsibleText,
        PostCommentEdit,
        PostImages,
    },
    props: {
        post: {
            type: Object,
            required: true,
        },
        comment: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            mouseIsOverBestCommentButton: false,
            edit: false,
            TagSize,
        };
    },
    computed: {
        ...mapGetters('auth', ['isAdmin']),
        ...mapGetters('ui', ['screen', 'screenSize', 'module']),
        postContextId() {
            return this.post[this.post.postDetails.context].id;
        },
        anonymousUser() {
            return this.comment.anonymousUser;
        },
        userPreview() {
            return this.comment.anonymousUser ? this.anonymousUser.user_preview : null;
        },
        isCommentOwner() {
            return this.comment.isOwner;
        },
        user() {
            return this.comment.user;
        },
        reported() {
            return this.comment.isReported && this.comment.userReport;
        },
        reportedLabel() {
            return this.reported ? this.$t('post:menu:reported') : this.$t('post:menu:report');
        },
        commentImages() {
            return this.comment.files;
        },
        canSelectBestAnswer() {
            return this.post.isOwner && this.post.postDetails.hasBestAnswer === false;
        },
        options() {
            const options = [
                {
                    label: this.$t('post:markBestAnswer'),
                    icon: 'checkmark-outlined',
                    onClick: this.toggleBestComment,
                    visible: this.canSelectBestAnswer && this.post.isOwner && this.screen.maxMD,
                },
                {
                    label: this.$t('post:unmarkBestAnswer'),
                    icon: 'checkmark-outlined',
                    onClick: this.toggleBestComment,
                    visible: this.comment.isBest && this.post.isOwner && this.screen.maxMD,
                },
                {
                    label: this.$t('post:menu:edit'),
                    icon: 'edit',
                    onClick: this.editComment,
                    visible: this.isAdmin || this.isCommentOwner,
                },
                {
                    label: this.reportedLabel,
                    icon: 'flag',
                    onClick: this.reportComment,
                    visible: this.isAdmin || !this.isCommentOwner,
                    class: this.reported ? 'text-red-400 pointer-events-none cursor-default' : '',
                },
                {
                    label: this.$t('post:menu:delete'),
                    icon: 'trash',
                    onClick: this.deleteComment,
                    visible: this.isAdmin || this.isCommentOwner,
                },
            ];
            return options.filter((items) => items.visible);
        },
        bestAnswerBtnClass() {
            return `${
                this.post.postDetails.context !== 'document' ? 'md:ml-1' : ''
            } whitespace-nowrap !text-sm mt-2 md:mt-0 w-32`;
        },
        isDocumentPage() {
            return route().current() !== 'document.show';
        },
        modalTranslation() {
            return {
                title: this.$t('post:popupDelete:headlineComment'),
                submitText: this.$t('post:popupDelete:submitComment'),
            };
        },
    },
    methods: {
        route,
        toggleBestComment() {
            this.$store.dispatch(`${this.module}/toggleBestComment`, {
                postId: this.post.postDetails.id,
                context: this.post.postDetails.context,
                commentId: this.comment.id,
            });
        },
        async deleteComment() {
            const confirmed = await this.$modal.confirmDanger({
                title: this.modalTranslation.title,
                submitText: this.modalTranslation.submitText,
            });

            if (!confirmed) return;

            await this.$store.dispatch(`${this.module}/deleteComment`, {
                context: this.post.postDetails.context,
                postId: this.post.postDetails.id,
                commentId: this.comment.id,
            });

            this.$store.dispatch('flashMessages/showSuccess', {
                content: this.$t('post:popupDelete:successComment'),
                progress: { time: 3000, showProgress: false },
            });
        },
        editComment() {
            this.edit = true;
            this.$nextTick(() => this.$refs.commentInput.focusCommentInput());
        },
        reportComment() {
            this.$modal.open(
                defineAsyncComponent(() => import('@/components/ModalReportPost')),
                {
                    postId: this.post.postDetails.id,
                    commentId: this.comment.id,
                    context: this.post.postDetails.context,
                    module: this.module,
                },
            );
        },
    },
};
</script>
